import {
  put, takeLatest, call, select, take,
} from 'redux-saga/effects';
import _get from 'lodash/get';
import api from '@/api/student';
import apiCourseSession from '@/api/course-session';
import { decodeInviteLink, hasInviteLink } from '@/utils/deencode-invite-link';

import {
  ERROR_PASS_REQUEST,
  ERROR_UPDATE_REQUEST,
  VALIDATION_ERROR_REQUEST,
} from '@/action-types/error';
import {
  STUDENT_GET_INFO_REQUEST,
  STUDENT_GET_INFO_SUCCESS,
  STUDENT_REGISTRATION_REQUEST,
  STUDENT_REGISTRATION_SUCCESS,
  STUDENT_REGISTRATION_SELF_REQUEST,
  STUDENT_REGISTRATION_BY_LINK_REQUEST,
  STUDENT_REGISTRATION_REPEAT,
  POST_REFERRALS_REQUEST,
  STUDENT_REGISTRATION_BY_LINK_SUCCESS,
  OPEN_STUDENT_PAGE,
  SET_COURSE_SESSION,
} from '@/action-types/student';
import {
  USER_ADD_DATA_SUCCESS,
} from '@/action-types/user';
import { Store } from '@/store/smartclass';
import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import { ANALYTICS_SEND_EVENT, ANALYTICS_SET_USER_PROPERTIES } from '@/action-types/analytics';
import { TEACHER_UPDATE_BALANCE, TEACHER_UPDATE_BALANCE_SUCCESS } from '@/action-types/teacher';
import { Action } from 'redux';
import { ReferralData } from '@/interfaces/dashboard/student-dashboard';
import { LOADING_UPDATE_REQUEST } from '@/action-types/loading';
import { REDIRECT_ACTION } from '@/action-types/redirect';
import authUtils from '@/utils/auth';
import { MAKE_BOOKING_SUCCESS } from '@/action-types/trial-lesson';
import { APP_START } from '@/action-types/app';

export function* getStudentInfo(params: { uid?: number; }) {
  try {
    const { user } = yield select();

    // @ts-ignore
    const response = yield call(api.getStudentInfo, params.uid || user.uid);
    const responseData = _get(response, 'data.getStudentInfo[0]', {});
    const responseDataUser = responseData.user;

    // @ts-ignore
    if (window.setSentryUserData) {
      // @ts-ignore
      window.setSentryUserData({
        email: responseDataUser?.username,
        role: 'teacher',
      });
    }

    delete responseData.user;

    yield put({ type: STUDENT_GET_INFO_SUCCESS, info: responseData });
    yield put({ type: USER_ADD_DATA_SUCCESS, userData: responseDataUser || {} });
    yield put({ type: ANALYTICS_SET_USER_PROPERTIES, data: { email: responseDataUser?.username } });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getStudentInfo'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export function* studentRegistration(params: {
  pathname: string;
}) {
  const hashData = yield call(decodeInviteLink, params?.pathname);

  if (!hashData || hashData.b2bCompanyId) {
    yield put({ type: STUDENT_REGISTRATION_SELF_REQUEST });
  } else {
    yield put({ type: STUDENT_REGISTRATION_SUCCESS });
  }
}

export function* studentInvite() {
  const hashString = window.location.pathname;
  const isInviteLink = yield call(hasInviteLink, hashString);
  const hashData = yield call(decodeInviteLink, hashString);
  const isAuth = !!authUtils.getAccessToken();

  const usedLinks = JSON.parse(localStorage.getItem('usedLinks') || '[]');

  if (hashData.b2bCompanyId) return;

  if (hashData && !usedLinks.includes(hashData?.encoded)) {
    const { student } = yield select();

    if (!student?.data?.id) yield take(STUDENT_GET_INFO_SUCCESS);

    yield put({
      type: STUDENT_REGISTRATION_BY_LINK_REQUEST,
      force: 0,
      hashData,
      hashString,
    });

    localStorage.setItem('usedLinks', JSON.stringify([...usedLinks, hashData?.encoded]));

    if (isAuth) {
      yield take(STUDENT_REGISTRATION_BY_LINK_SUCCESS);

      yield put({
        type: REDIRECT_ACTION,
        payload: {
          path: '/student',
        },
      });
    }
  } else if (isInviteLink) {
    yield put({
      type: REDIRECT_ACTION,
      payload: {
        path: '/student',
      },
    });
  }
}

export function* studentRegistrationByLink(params: {
  hashData: {
    courseId: string;
    teacherId: string;
  };
  hashString: string;
  force?: number;
}) {
  try {
    const { student } = yield select();

    yield call(api.registerStudent, {
      teacherId: parseInt(params.hashData.teacherId, 10),
      studentId: student.data.id,
      courseId: parseInt(params.hashData.courseId, 10),
      force: params.force || 0,
    });

    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'add-student-by-link',
      data: {
        force: params.force || 0,
        courseId: parseInt(params.hashData.courseId, 10),
        hash: params.hashString,
        studentId: student.data.id,
        teacherId: parseInt(params.hashData.teacherId, 10),
      },
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'studentRegistrationByLink'] });

    let is420 : boolean | undefined;

    e.graphQLErrors.forEach((error: {
      extensions: { status: number; };
    }) => {
      switch (error.extensions.status) {
        case 420:
          is420 = true;
          break;
        default:
        // nothing
      }
    });

    if (is420) {
      yield put({
        type: STUDENT_REGISTRATION_REPEAT,
        hashData: params.hashData,
        hashString: params.hashString,
      });
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    }
  } finally {
    yield put({
      type: STUDENT_REGISTRATION_BY_LINK_SUCCESS,
    });
  }
}

export function* studentRegistrationRepeat(params: {
  hashData: {
    courseId: string;
    teacherId: string;
  };
  hashString: string;
}) {
  const isConfirm = window.confirm('You\'re want to change teacher on your course. Are you sure?');

  if (isConfirm) {
    yield put({
      type: STUDENT_REGISTRATION_BY_LINK_REQUEST,
      force: 1,
      hashData: params.hashData,
      hashString: params.hashString,
    });
  }
}

export function* studentRegistrationSelf() {
  try {
    const { student } = yield select();

    yield call(api.registerStudentSelf, {
      studentId: student.data.id,
      courseId: 3,
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'studentRegistrationSelf'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  } finally {
    yield put({ type: STUDENT_REGISTRATION_SUCCESS });
  }
}

export function getData(getCourseSession) {
  let data = [];
  // если еще не выбран текущий курс то выбрать его по бизнесс логике
  // БЛ
  // Если у студента не один курс ТО
  // Группируем по признаку есть тичер или нет
  // Каждую группу сортируем по дате апдейта данных
  // Группу с тичером показываем первой
  // Все курсы с тичером - показываем всегда
  // Курсы без тичера показываем только те в которых пройден хотя бы 1 урок

  // Если будет ситуация что первой группы нет,
  // во второй группе надо показать 1 курс где есть пройденые уроки

  // если
  // во второй группе нет курсов в которой есть пройденые уроки
  // то показываем 1 курс по последней дате создания/обновлене
  // Если у студента 1 курс - просто его показываем
  if (getCourseSession?.length === 1) {
    data = getCourseSession;
  } else if (getCourseSession?.length > 1) {
    const sorted = _sortBy(
      getCourseSession,
      (courseSession) => new Date(courseSession.updatedAt),
    ).reverse();

    const grouped = _groupBy(
      sorted,
      (courseSession) => (courseSession.studentsList.length !== 0 ? 'withTeacher' : 'withoutTeacher'),
    );

    // группа с тичерами если есть то добавляем в любом случае
    if (grouped.withTeacher) {
      data = data.concat(grouped.withTeacher);
    }

    // группу без тичеров добавляем только где есть прогрес который не ноль
    if (grouped.withoutTeacher) {
      const courseWithProgress = grouped.withoutTeacher
        .filter((courseSession) => courseSession.progress > 0);

      // во второй группе пройденые уроки?
      if (courseWithProgress.length > 0) {
        data = data.concat(courseWithProgress);
      }
    }

    if (data.length === 0) {
      const courseWithoutProgress = grouped.withoutTeacher
        .filter((courseSession) => courseSession.progress === 0);

      data = data.concat(courseWithoutProgress[0] || []);
    }
  }

  data = data.filter((el) => el.studentsList?.length);
  const defaultData = getCourseSession.filter((el) => el.course?.id === 3);

  return data.length > 0 ? data : defaultData;
}

function* getCourseSessionFn() {
  const { student } = yield select();

  if (!student.data.id) {
    // ждем student data id
    yield take(STUDENT_GET_INFO_SUCCESS);

    yield put({ type: OPEN_STUDENT_PAGE });
  } else {
    const { student: studentStore } : Store = yield select();

    let response : {} | undefined;
    // получаем данные CourseSession
    try {
      // @ts-ignore
      response = yield call(apiCourseSession.getCourseSession, parseInt(studentStore.data.id, 10));
    } catch (e) {
      console.log(e);
    }

    if (response) {
      // @ts-ignore
      const { getCourseSession } = response.data;

      const data = getData(getCourseSession);

      if (data[0] && data[0].id) {
        // тут важен порядок
        // вначале сетим все курсы, потом находим первый
        yield put({ type: SET_COURSE_SESSION, data, currentCourseId: data[0].id });
      }
    }
  }
}

function* studentBalanceUpdate(params: Action & { studentId: number; balance: number; }) {
  try {
    // @ts-ignore
    yield call(
      api.studentBalanceUpdate,
      { studentId: params.studentId, balance: params.balance },
    );

    const { teacher: { students } } = yield select();
    const index = students?.data.findIndex(
      (student: any) => student.session.student.id === params.studentId,
    );

    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'teacher-update-student-balance',
      data: { studentId: params.studentId, balance: params.balance },
    });

    if (index !== -1) {
      students.data[index].session.student.balance = params.balance;
      yield put({ type: TEACHER_UPDATE_BALANCE_SUCCESS, students });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'studentBalanceUpdate'] });
    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'teacher-update-student-balance-error',
      data: { studentId: params.studentId, balance: params.balance },
    });

    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

function* postReferral(params: Action & { data: ReferralData; cbSuccessSend: () => void; }) {
  yield put({ type: LOADING_UPDATE_REQUEST, name: 'student', state: true });
  try {
    const response = yield call(api.postReferrals, params.data);

    if (response.data.postReferrals) {
      params.cbSuccessSend();

      yield put({
        type: ANALYTICS_SEND_EVENT,
        eventName: 'invite_friend_submit',
        data: params.data,
      });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'postReferral'] });
    yield put({ type: VALIDATION_ERROR_REQUEST, isActive: true, errorData: e });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'student', state: false });
  }
}

export default function* studentSaga() {
  // @ts-ignore
  yield takeLatest(STUDENT_GET_INFO_REQUEST, getStudentInfo);
  yield takeLatest(APP_START, studentInvite);
  // @ts-ignore
  yield takeLatest(STUDENT_REGISTRATION_REQUEST, studentRegistration);
  yield takeLatest(STUDENT_REGISTRATION_SELF_REQUEST, studentRegistrationSelf);
  // @ts-ignore
  yield takeLatest(STUDENT_REGISTRATION_BY_LINK_REQUEST, studentRegistrationByLink);
  // @ts-ignore
  yield takeLatest(STUDENT_REGISTRATION_REPEAT, studentRegistrationRepeat);
  yield takeLatest(OPEN_STUDENT_PAGE, getCourseSessionFn);
  yield takeLatest(MAKE_BOOKING_SUCCESS, getCourseSessionFn);
  yield takeLatest(TEACHER_UPDATE_BALANCE, studentBalanceUpdate);
  yield takeLatest(POST_REFERRALS_REQUEST, postReferral);
}
